import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Settings } from '../../core';
import { AddressField, SiteShippingAddressFetchRequest, SiteShipmentCourierData, Country } from './service/form-field';
import { map } from 'rxjs/operators';
import { dataMember, typedArray } from 'santee-dcts';
import { ShipmentFormData } from '../interfaces/shipment-form-data';
import { SiteShippingBarcodeDetailsData } from './service/container-barcode-info';
import { uniqBy } from 'lodash';
import { WayBillValidatorDataModel, WayBillValidatorData } from '../interfaces/shipment-data';

export class SiteShippingAddressModel {
    @dataMember()
    @typedArray(AddressField)
    public addressList: AddressField[];

    @dataMember()
    @typedArray(AddressField)
    public shippedToAddressData: AddressField[];

}

export class SiteShipmentCouriersReadModel {
    @dataMember()
    @typedArray(SiteShipmentCourierData)
    public courierData: SiteShipmentCourierData[];
}

export class CountryListModel {
    @dataMember()
    @typedArray(Country)
    public countryList: Country[];
}

@Injectable()
export class ShipmentFormStorage {

    public siteShippingUrl = `${this.settings.apiUrl}/investigator/siteshipping`;
    public sampleManagementUrl = `${this.settings.apiUrl}/investigator/sampleregistration`;

    constructor(
        private settings: Settings,
        private http: HttpClient) {
    }

    public getShippingFromAddress(containerNumber: string[]): Observable<any> {
        containerNumber.forEach((val: string) => {
            val = encodeURI(val.toString());
        });
        let request: SiteShippingAddressFetchRequest = {
            containerBarcodeList: containerNumber
        };
        let addressFilterList: { key: number; value: string, address: AddressField }[] = [];
        return this.http.post(`${this.siteShippingUrl}/investigatoraddress`, request).pipe(
            map((rawData: SiteShippingAddressModel) => {
                if (!rawData.addressList) {
                    addressFilterList.push(this.customAddressItem());
                    return addressFilterList;
                }
                rawData.addressList.forEach((address, index) => {
                    let dropDownValue = this.getAddressValue(address);
                    if (dropDownValue.length > 0) {
                        let item = {
                            key: index + 1,
                            value: dropDownValue,
                            address: address
                        };
                        addressFilterList.push(item);
                    }
                });
                addressFilterList.push(this.customAddressItem());
                return addressFilterList;
            })
        );
    }

    public getDestinationAddress(): Observable<any> {
        let addressFilterList: { key: number; value: string, address: AddressField }[] = [];
        return this.http.get(`${this.siteShippingUrl}/previousaddress`).pipe(
            map((rawData: SiteShippingAddressModel) => {
                if (!rawData.addressList) {
                    addressFilterList.push(this.otherAddressItem());
                    return addressFilterList;
                }
                rawData.addressList.forEach((address, index) => {
                    let dropDownValue = this.getAddressValue(address);
                    if (dropDownValue.length > 0 && (addressFilterList.findIndex(t => t.value === dropDownValue) === -1)) {
                        let item = {
                            key: addressFilterList.length + 1,
                            value: dropDownValue,
                            address: address
                        };
                        addressFilterList.push(item);
                    }
                });
                addressFilterList.push(this.otherAddressItem());
                return addressFilterList;
            })
        );
    }

    public isAribillNumberValid(airbillNumber: string, courier: string): Observable<any> {
        return this.http.get(`${this.siteShippingUrl}/airbillnumber/${airbillNumber}`).pipe(
            map((rawData: WayBillValidatorDataModel) => {
                if (rawData && rawData.waybillValidatorData) {
                    if (courier && courier.length) {
                        let filteredData = rawData.waybillValidatorData.find(t => t.courierName.toLowerCase() === courier.toLowerCase());
                        if (filteredData) {
                            return { courierData: rawData.waybillValidatorData, isValid: true };
                        }
                    }
                    return { courierData: rawData.waybillValidatorData, isValid: false };
                }
                return { courierData: null, isValid: false };
            })
        );
    }

    public validateAirbillNumber(airbillNumber: string, courier: any): Observable<boolean> {
        let request = {
            airBillNumber: airbillNumber,
            courierName: courier.value,
            courierId: courier.lgxCourierId
        };
        return this.http.post(`${this.siteShippingUrl}/validateairbill`, request).pipe(map((result: any) => {
            return result.isValidAirBillNumber;
        }));
    }

    public validateCourier(courier: string, courierData: WayBillValidatorData[]): boolean {
        let filteredData = courierData.find(t => t.courierName.toLowerCase() === courier.toLowerCase());
        if (filteredData) {
            return true;
        }
        return false;
    }


    public getToLabcorpAddress(barcodeList: string[]): Observable<any> {
        barcodeList.forEach((val: string) => {
            val = encodeURI(val.toString());
        });
        let request = { barcodes: barcodeList };
        let addressFilterList: { key: number; value: string, address: AddressField }[] = [];
        return this.http.post(`${this.siteShippingUrl}/shippedtolabcorplocations`, request).pipe(
            map((rawData: SiteShippingAddressModel) => {
                if (!rawData.shippedToAddressData) {
                    addressFilterList.push(this.customAddressItem());
                    return addressFilterList;
                }
                rawData.shippedToAddressData.forEach((address, index) => {
                    let dropDownValue = this.getAddressValue(address, true);
                    if (dropDownValue.length > 0) {
                        let item = {
                            key: index + 1,
                            value: dropDownValue,
                            address: address
                        };
                        addressFilterList.push(item);
                    }
                });
                addressFilterList.push(this.customAddressItem());
                return addressFilterList;
            })
        );
    }

    public getCourier(barcodeList: string[]): Observable<SiteShipmentCourierData[]> {
        barcodeList.forEach((val: string) => {
            val = encodeURI(val.toString());
        });
        let request = { barcodes: barcodeList };
        return this.http.post(`${this.siteShippingUrl}/couriers`, request).pipe(
            map((rawData: SiteShipmentCouriersReadModel) => {
                if (!rawData || !rawData.courierData) {
                    return [];
                }
                return rawData.courierData;
            })
        );
    }

    public getContainerSearchData(formatedBarcode: any): Observable<any> {
        return this.http.post(`${this.siteShippingUrl}/barcode`, formatedBarcode);
    }

    public getContainerTransferReason(): Observable<any> {
        return this.http.get(`${this.siteShippingUrl}/containers-reasons`);
    }

    public getInprogressShipmentDetails(id: number): Observable<any> {
        return this.http.get(`${this.siteShippingUrl}/inprogressdetails/${id}`);
    }

    public getAddressValue(address: AddressField, isToAddress = false) {
        let value = '';
        if (isToAddress) {
            return address.labSiteName ? address.labSiteName + ' - Preferred' : address.companyName + ' - Preferred';
        }
        if (address.companyName) {
            value = value + address.companyName + ', ';
        }
        if (address.departmentName) {
            value = value + address.departmentName + ', ';
        }
        if (address.addressLineOne) {
            value = value + address.addressLineOne + ', ';
        }
        if (address.addressLineTwo) {
            value = value + address.addressLineTwo + ', ';
        }
        if (address.city) {
            value = value + address.city + ', ';
        }
        if (address.state) {
            value = value + address.state + ', ';
        }
        if (address.countryName) {
            value = value + address.countryName + ', ';
        }
        if (address.zipCode) {
            value = value + address.zipCode;
        }
        return value;
    }

    public customAddressItem() {
        return {
            key: 100,
            value: 'Enter Custom Address',
            address: new AddressField()
        };
    }

    public otherAddressItem() {
        return {
            key: 100,
            value: 'Others',
            address: new AddressField()
        };
    }

    public createShipment(shipmentFormData: ShipmentFormData) {
        return this.http.post(`${this.siteShippingUrl}/submitshipment`, shipmentFormData);
    }

    public loadCountryList() {
        return this.http.get(`${this.siteShippingUrl}/countrylist`).pipe(
            map((rawData: CountryListModel) => {
                rawData.countryList.forEach(data => {
                    data.id = data.countryId;
                    data.value = data.countryName;
                });
                return rawData.countryList;
            }) 
        );
    }

    public deleteShipment(shipmentId: number) {
        return this.http.delete(`${this.siteShippingUrl}/deleteshipment/${shipmentId}`);
    }

    public deleteContainerFromShipment(shipmentId: number, containerId: number) {
        return this.http.delete(`${this.siteShippingUrl}/deleteshipment/container/${shipmentId}/${containerId}`);
    }

    public getRegisteredSamplesList(): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/getregisteredsamples`, {}).pipe(map((result: any) => {
            return result.sampleRegistrationRegisteredSampleData;
        }));
    }
}